import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, SlidersHorizontal, ArrowUp, ArrowDown, Info } from 'lucide-react'
import { labelCls } from '../styles'
import { DEFAULT_CASCADE_CHECKS } from '../data'
import type { CascadeCheckConfig } from '../types'
import type { DerivedKycStep } from '../workflowDerivedConfig'
import { BuilderLockedBanner } from './BuilderLockedBanner'
import { applyIdSuggestions, useAiSeed } from '../aiConfigure'
import type { AiIdSuggestion, AiSeed } from '../aiConfigure'

interface CascadeConfigPanelProps {
  onChecksChange: (checks: CascadeCheckConfig[]) => void
  /** When set, the workflow picked in Workflow Builder already decided this
   * cascade's steps — show them read-only instead of the editable checklist. */
  derivedSteps?: DerivedKycStep[] | null
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<AiIdSuggestion[]> | null
}

export function CascadeConfigPanel({ onChecksChange, derivedSteps, aiSeed }: CascadeConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [checks, setChecks] = useState<CascadeCheckConfig[]>(DEFAULT_CASCADE_CHECKS)
  const [desiredPassRate, setDesiredPassRate] = useState(85)
  const locked = !!derivedSteps && derivedSteps.length > 0

  useAiSeed(aiSeed, (suggestions) => setChecks((prev) => applyIdSuggestions(prev, suggestions)))

  useEffect(() => {
    onChecksChange(checks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checks])

  function toggleCheck(id: string) {
    setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)))
  }

  function move(id: string, dir: -1 | 1) {
    setChecks((prev) => {
      const idx = prev.findIndex((c) => c.id === id)
      const swapWith = idx + dir
      if (idx < 0 || swapWith < 0 || swapWith >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[swapWith]] = [next[swapWith], next[idx]]
      return next
    })
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Cascade Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {locked ? (
            <>
              <BuilderLockedBanner>
                This cascade&apos;s steps come from the workflow already configured in Workflow Builder — edit them there to change what runs.
              </BuilderLockedBanner>
              <div>
                <label className={labelCls}>KYC steps in this workflow · {derivedSteps!.length}</label>
                <div className="space-y-1.5">
                  {derivedSteps!.map((step, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                      <span className="text-[9px] font-mono text-[#475569] w-4 shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-[#1e293b]">{step.provider ?? 'KYC check'}</p>
                        {step.ruleset && <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">Ruleset: {step.ruleset}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
          <>
          <p className="text-[10px] text-[#475569] flex items-start gap-1.5">
            <Info size={11} className="shrink-0 mt-0.5" />
            A simplified stand-in for Operations Center&apos;s cascade/workflow design — reorder and toggle the checks a verification waterfall typically runs, without the full drag-and-drop workflow builder. The real workflow picked below still drives the live demo call.
          </p>

          <div>
            <label className={labelCls}>Checks in this cascade · {checks.filter((c) => c.enabled).length}/{checks.length} enabled</label>
            <div className="space-y-1.5">
              {checks.map((check, i) => (
                <div
                  key={check.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]"
                  style={check.enabled
                    ? { background: '#f8fafc', border: '1px solid #e5e7eb' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.55 }
                  }
                >
                  <span className="text-[9px] font-mono text-[#475569] w-4 shrink-0">{i + 1}</span>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button onClick={() => move(check.id, -1)} disabled={i === 0} className="text-[#475569] hover:text-[#475569] disabled:opacity-30 disabled:hover:text-[#475569]">
                      <ArrowUp size={11} />
                    </button>
                    <button onClick={() => move(check.id, 1)} disabled={i === checks.length - 1} className="text-[#475569] hover:text-[#475569] disabled:opacity-30 disabled:hover:text-[#475569]">
                      <ArrowDown size={11} />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#1e293b]">{check.label}</p>
                    <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{check.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={check.enabled} onChange={() => toggleCheck(check.id)} className="sr-only peer" />
                    <div className="w-8 h-[18px] rounded-full transition-colors peer-checked:bg-[#6366f1]" style={{ background: check.enabled ? undefined : '#e5e7eb' }} />
                    <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: check.enabled ? 'translateX(14px)' : 'translateX(0)' }} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Desired pass rate — {desiredPassRate}%</label>
            <input
              type="range"
              min={50}
              max={100}
              step={5}
              value={desiredPassRate}
              onChange={(e) => setDesiredPassRate(Number(e.target.value))}
              className="w-full"
              style={{ accentColor: '#6366f1' }}
            />
            <p className="text-[10px] text-[#475569] mt-1">The proportion of genuine applicants expected to clear this cascade automatically, without manual review.</p>
          </div>
          </>
          )}
        </div>
      )}
    </div>
  )
}
